import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Animated,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  AntDesign,
  Ionicons,
  MaterialIcons,
  Octicons,
} from "@expo/vector-icons";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import IconEmptyView from "../../Components/IconEmptyView";
import LoadingLogo from "../../Components/LoadingLogo";
import ThreeDotLoader from "../../Components/ThreeDotLoader";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { useDevice, useScreenDimensions } from "../../_layout";
import useChevronRotation from "../../hooks/useChevronRotation";
import AddFoodModal from "./editor/AddFoodModal";
import {
  FOOD_SENTINEL_ROOT_MARGIN,
  isCatalogFood,
} from "./foodCatalog";
import {
  buildFoodBrowserItems,
  getFoodBrowserItemKey,
} from "./foodBrowser";
import { buildCanonicalFoodFilters } from "./foodSearchService";
import { useCanonicalFoodSearch } from "./useCanonicalFoodSearch";
import {
  formatCalorieValue,
  formatMacroValue,
  UI_MACRO_DISPLAY_ORDER,
  UI_MACRO_SHORT_LABELS,
} from "../shared/nutritionFormatters";
import {
  getSearchEnterTarget,
  isEnterKeyEvent,
} from "../shared/searchEnterTarget";
import { NutritionLibrarySkeletonGrid } from "../shared/NutritionLibrarySkeletonGrid";
import { useDesktopAutoFocusTextInput } from "../shared/useDesktopAutoFocusTextInput";

const FOOD_FILTER_KEYS = [
  "all-string",
  "food-string",
  "supplement-string",
  "createdByMe-string",
];

const FOOD_FILTER_CONFIG = {
  "all-string": {
    icon: "grid-outline",
  },
  "food-string": {
    icon: "nutrition-outline",
  },
  "supplement-string": {
    icon: "flask-outline",
  },
  "createdByMe-string": {
    icon: "person-outline",
  },
};

const FOOD_SORT_OPTIONS = [
  {
    key: "default",
    labelKey: "default-string",
    field: null,
  },
  {
    key: "energy",
    labelKey: "energy-string",
    field: "calories",
  },
  ...UI_MACRO_DISPLAY_ORDER.map((field) => ({
    key: field,
    labelKey: `${field}-string`,
    field,
  })),
];

function useEndReachedObserver({ enabled, onEndReached }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const target = sentinelRef.current;
    if (!target) {
      return undefined;
    }

    let cancelled = false;
    let rafId = null;

    const triggerEndReached = () => {
      if (!cancelled) {
        onEndReached();
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      rafId = requestAnimationFrame(() => {
        const top = target?.getBoundingClientRect?.()?.top;
        if (typeof top === "number" && top <= window.innerHeight + 320) {
          triggerEndReached();
        }
      });

      return () => {
        cancelled = true;
        if (rafId != null) {
          cancelAnimationFrame(rafId);
        }
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          triggerEndReached();
        }
      },
      {
        root: null,
        rootMargin: FOOD_SENTINEL_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(target);

    rafId = requestAnimationFrame(() => {
      const top = target?.getBoundingClientRect?.()?.top;
      if (typeof top === "number" && top <= window.innerHeight + 320) {
        triggerEndReached();
      }
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [enabled, onEndReached]);

  return sentinelRef;
}

const FoodCard = React.memo(
  ({
    item,
    t,
    onPress,
    screenSize,
    isMobile,
    language,
    isEnterTarget = false,
    onHoverStart,
    onHoverEnd,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isCreatedByMe = item?.sourceKind === "coach";
    const databaseLabel = item?.sourceLabel ?? "";
    const displayName = item?.name || t("foods-string");
    const macroValues = {
      protein: item?.nutrients?.protein,
      carbs: item?.nutrients?.carbs,
      fats: item?.nutrients?.fats,
    };
    const columnCount = screenSize === "small" ? 1 : 2;
    const gap = screenSize === "small" ? 8 : 12;
    const cardWidth = `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`;

    return (
      <View style={{ width: cardWidth }}>
        <TouchableOpacity
          activeOpacity={0.96}
          onPress={onPress}
          onMouseEnter={
            !isMobile
              ? () => {
                  setIsHovered(true);
                  onHoverStart?.();
                }
              : undefined
          }
          onMouseLeave={
            !isMobile
              ? () => {
                  setIsHovered(false);
                  onHoverEnd?.();
                }
              : undefined
          }
          style={styles.cardTouchable}
        >
          <View
            style={[
              styles.card,
              isHovered && styles.cardHovered,
              isEnterTarget && styles.cardEnterTarget,
            ]}
          >
            <View style={styles.cardLeft}>
              <Text numberOfLines={1} style={styles.cardName}>
                {displayName}
              </Text>
              <Text
                numberOfLines={1}
                style={[
                  styles.cardDatabase,
                  isCreatedByMe && styles.cardDatabaseCreatedByMe,
                ]}
              >
                {databaseLabel}
              </Text>
            </View>
            <View style={styles.cardRight}>
              <Text style={styles.cardKcal}>
                {formatCalorieValue(item?.nutrients?.calories)}
                <Text style={styles.cardKcalUnit}> kcal</Text>
              </Text>
              <View style={styles.cardMacroRow}>
                {UI_MACRO_DISPLAY_ORDER.map((key) => (
                  <Text key={key} style={styles.cardMacroValue}>
                    {UI_MACRO_SHORT_LABELS[key]}{" "}
                    <Text style={styles.cardMacroNum}>
                      {formatMacroValue(macroValues[key])}g
                    </Text>
                  </Text>
                ))}
              </View>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  },
);

export const FoodsView = observer(({ coach }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile, isIOS } = useDevice();
  const [selectedFilter, setSelectedFilter] = useState("all-string");
  const [selectedSortKey, setSelectedSortKey] = useState("default");
  const [isSortTriggerHovered, setIsSortTriggerHovered] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [hoveredFilterKey, setHoveredFilterKey] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [foodModalTarget, setFoodModalTarget] = useState();
  const [enterTargetOverrideKey, setEnterTargetOverrideKey] = useState(null);
  const [isCreateButtonHovered, setIsCreateButtonHovered] = useState(false);
  const searchInputRef = useRef(null);
  const isSearchInputPressingRef = useRef(false);
  const sortChevronRotation = useChevronRotation(
    isSortMenuOpen,
    "closedDownOpenUp",
  );
  useDesktopAutoFocusTextInput(searchInputRef, { isMobile });
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const sortOptions = useMemo(
    () =>
      FOOD_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const selectedSortOption =
    sortOptions.find((option) => option.key === selectedSortKey) ||
    sortOptions[0];
  const isFoodModalVisible = foodModalTarget !== undefined;

  const coachFoods = Array.isArray(coach?.nutritionStore?.sortedFoods)
    ? coach.nutritionStore.sortedFoods
    : [];
  const shouldShowInitialSkeletons =
    coach?.nutritionStore?.isFetchingFoods === true &&
    coach?.nutritionStore?.hasFetchedFoods !== true;
  const selectedFilters = useMemo(() => {
    if (selectedFilter === "food-string") {
      return buildCanonicalFoodFilters({ type: "food" });
    }

    if (selectedFilter === "supplement-string") {
      return buildCanonicalFoodFilters({ type: "supplement" });
    }

    if (selectedFilter === "createdByMe-string") {
      return buildCanonicalFoodFilters({ source: "coach" });
    }

    return buildCanonicalFoodFilters();
  }, [selectedFilter]);
  const modalFood = useMemo(() => {
    if (foodModalTarget === undefined || foodModalTarget === null) {
      return null;
    }

    if (foodModalTarget.kind === "coach") {
      return (
        coachFoods.find((food) => food.id === foodModalTarget.foodId) ??
        foodModalTarget.fallbackRecord ??
        null
      );
    }

    return foodModalTarget.sourceRecord ?? null;
  }, [coachFoods, foodModalTarget]);
  const canonicalFoodSearch = useCanonicalFoodSearch({
    coachFoods,
    query: searchText,
    filters: selectedFilters,
    sortField: selectedSortOption?.field,
    pageSize: 40,
    surface: "library",
    language,
  });
  const browserItems = useMemo(
    () =>
      buildFoodBrowserItems(canonicalFoodSearch.items, {
        language,
        t,
      }),
    [canonicalFoodSearch.items, language, t],
  );
  const enterTargetFood = useMemo(
    () =>
      getSearchEnterTarget({
        searchText,
        items: browserItems,
        getItemKey: getFoodBrowserItemKey,
        overrideKey: enterTargetOverrideKey,
      }),
    [browserItems, enterTargetOverrideKey, searchText],
  );
  const hasEnterTarget = enterTargetFood != null;
  const enterTargetFoodKey = getFoodBrowserItemKey(enterTargetFood);
  const hasActiveSearch = searchText.trim().length > 0;
  const shouldShowPendingLoader =
    !shouldShowInitialSkeletons &&
    canonicalFoodSearch.isDeferringItems === true &&
    browserItems.length === 0;

  useEffect(() => {
    setEnterTargetOverrideKey(null);
  }, [searchText]);

  const endReachedSentinelRef = useEndReachedObserver({
    enabled:
      !shouldShowInitialSkeletons &&
      canonicalFoodSearch.hasMore &&
      browserItems.length > 0,
    onEndReached: canonicalFoodSearch.loadMore,
  });
  const openCreateFoodModal = useCallback(() => {
    setFoodModalTarget(null);
  }, []);
  const openEditFoodModal = useCallback((food) => {
    if (!food) {
      setFoodModalTarget(null);
      return;
    }

    if (isCatalogFood(food)) {
      setFoodModalTarget({
        kind: "catalog",
        sourceRecord: food,
      });
      return;
    }

    setFoodModalTarget({
      kind: "coach",
      foodId: food.id ?? null,
      fallbackRecord: food,
    });
  }, []);
  const openFirstVisibleFood = useCallback(() => {
    if (searchText.trim().length === 0) {
      return;
    }

    if (!enterTargetFood) {
      return;
    }

    openEditFoodModal(enterTargetFood.sourceRecord);
  }, [enterTargetFood, openEditFoodModal, searchText]);
  const closeFoodModal = useCallback(() => {
    setFoodModalTarget(undefined);
  }, []);
  const handleSearchKeyPress = useCallback(
    (event) => {
      if (!isEnterKeyEvent(event)) {
        return;
      }

      if (
        shouldShowInitialSkeletons ||
        searchText.trim().length === 0 ||
        browserItems.length === 0
      ) {
        return;
      }

      event?.preventDefault?.();
      openFirstVisibleFood();
    },
    [browserItems.length, openFirstVisibleFood, searchText, shouldShowInitialSkeletons],
  );
  const focusSearchInput = useCallback(() => {
    if (isSearchInputPressingRef.current) {
      return;
    }

    searchInputRef.current?.focus();
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.headerCard}>
        <View style={styles.searchRow}>
          <TouchableOpacity
            activeOpacity={1}
            onPress={focusSearchInput}
            style={styles.searchField}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={t("searchByName-string")}
            aria-label={t("searchByName-string")}
          >
            <View style={styles.searchIcon}>
              <Octicons name="search" size={14} color={Colors.grayColor} />
            </View>
            <TextInput
              ref={searchInputRef}
              value={searchText}
              onChangeText={setSearchText}
              onKeyPress={handleSearchKeyPress}
              onPressIn={() => {
                isSearchInputPressingRef.current = true;
              }}
              onPressOut={() => {
                isSearchInputPressingRef.current = false;
              }}
              onBlur={() => {
                isSearchInputPressingRef.current = false;
              }}
              placeholder={t("searchByName-string")}
              placeholderTextColor={Colors.grayColor}
              style={[
                styles.searchInput,
                isMobile && isIOS ? styles.searchInputIOS : null,
              ]}
              accessibilityLabel={t("searchByName-string")}
              aria-label={t("searchByName-string")}
            />
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setIsCreateButtonHovered(false);
              openCreateFoodModal();
            }}
            onMouseEnter={!isMobile ? () => setIsCreateButtonHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsCreateButtonHovered(false) : undefined}
          >
            <View
              style={[
                styles.createButton,
                isCreateButtonHovered ? styles.createButtonHovered : null,
              ]}
            >
              <AntDesign name="plus" size={12} color={Colors.whiteColor} />
              <Text style={styles.createButtonText}>{t("create-string")}</Text>
            </View>
          </TouchableOpacity>
        </View>
        <View style={styles.divider} />
        <View style={styles.bottomRow}>
          <View style={styles.filters}>
            {FOOD_FILTER_KEYS.map((filterKey) => {
              const isActive = selectedFilter === filterKey;
              const isHovered = !isMobile && hoveredFilterKey === filterKey;
              const useHighlight = isActive || isHovered;
              return (
                <TouchableOpacity
                  key={filterKey}
                  activeOpacity={0.8}
                  onPress={() => setSelectedFilter(filterKey)}
                  onMouseEnter={
                    !isMobile ? () => setHoveredFilterKey(filterKey) : undefined
                  }
                  onMouseLeave={
                    !isMobile ? () => setHoveredFilterKey(null) : undefined
                  }
                  style={[
                    styles.filterItem,
                    isActive && styles.filterItemActive,
                  ]}
                >
                  <Ionicons
                    name={FOOD_FILTER_CONFIG[filterKey].icon}
                    size={14}
                    color={
                      useHighlight ? Colors.bubbleColor : Colors.darkgrayColor
                    }
                  />
                  <Text
                    style={[
                      styles.filterText,
                      useHighlight && styles.filterTextActive,
                    ]}
                  >
                    {t(filterKey)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <FloatingMenu
            placement="bottom-end"
            width={220}
            items={sortOptions}
            open={isSortMenuOpen}
            onOpenChange={setIsSortMenuOpen}
            trigger={
              <View
                style={styles.sortTrigger}
                onMouseEnter={!isMobile ? () => setIsSortTriggerHovered(true) : undefined}
                onMouseLeave={!isMobile ? () => setIsSortTriggerHovered(false) : undefined}
              >
                <MaterialIcons
                  name="sort"
                  size={14}
                  color={Colors.darkgrayColor}
                />
                {selectedSortOption?.field == null ? (
                  <Text
                    style={[
                      styles.sortTriggerLabel,
                      isSortTriggerHovered ? styles.sortTriggerLabelHovered : null,
                    ]}
                  >
                    {t("sortBy-string")}
                  </Text>
                ) : (
                  <Text style={styles.sortTriggerValue}>
                    {selectedSortOption?.label}
                  </Text>
                )}
                <Animated.View
                  style={{ transform: [{ rotate: sortChevronRotation }] }}
                >
                  <Ionicons
                    name="chevron-down"
                    size={14}
                    color={
                      isSortTriggerHovered ? Colors.darkgrayColor : Colors.grayColor
                    }
                  />
                </Animated.View>
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.sortMenuItem}
                hoverStyle={styles.sortMenuItemHover}
                onPress={() => {
                  setSelectedSortKey(item.key);
                  close();
                }}
              >
                <Text
                  style={[
                    styles.sortMenuItemText,
                    item.key === selectedSortKey
                      ? styles.sortMenuItemTextActive
                      : null,
                  ]}
                >
                  {item.label}
                </Text>
                {item.key === selectedSortKey ? (
                  <Ionicons
                    name="checkmark"
                    size={15}
                    color={Colors.bubbleColor}
                  />
                ) : null}
              </HoverableTouchableOpacity>
            )}
          />
        </View>
      </View>

      <View style={styles.results}>
        {shouldShowInitialSkeletons ? (
          <NutritionLibrarySkeletonGrid screenSize={screenSize} variant="food" />
        ) : shouldShowPendingLoader ? (
          <View style={styles.searchPendingState}>
            <ThreeDotLoader
              key={`food-library-loading-session-${canonicalFoodSearch.loadingSessionId ?? 0}`}
              color={Colors.bubbleColor}
            />
          </View>
        ) : !hasActiveSearch &&
          canonicalFoodSearch.isLoading &&
          browserItems.length === 0 &&
          coachFoods.length === 0 ? (
          <NutritionLibrarySkeletonGrid screenSize={screenSize} variant="food" />
        ) : browserItems.length === 0 &&
          searchText.trim().length === 0 &&
          selectedFilter === "all-string" ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="nutrition-outline"
            mainText="foodLibraryEmptyTitle-string"
            subText="foodLibraryEmptyDescription-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : browserItems.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="search-outline"
            mainText="noFoodsFound-string"
            subText="adjustFiltersOrSearch-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : (
          <View
            style={[
              styles.foodCardGrid,
              { gap: screenSize === "small" ? 8 : 12 },
            ]}
          >
            {browserItems.map((item, index) => (
              <FoodCard
                key={getFoodBrowserItemKey(item) || index}
                item={item}
                t={t}
                screenSize={screenSize}
                isMobile={isMobile}
                language={language}
                isEnterTarget={
                  hasEnterTarget &&
                  getFoodBrowserItemKey(item) === enterTargetFoodKey
                }
                onHoverStart={() =>
                  setEnterTargetOverrideKey(getFoodBrowserItemKey(item))
                }
                onPress={() => openEditFoodModal(item.sourceRecord)}
              />
            ))}
          </View>
        )}

        {!shouldShowInitialSkeletons && canonicalFoodSearch.hasMore ? (
          <View
            ref={endReachedSentinelRef}
            collapsable={false}
            style={styles.loadMoreSentinel}
          >
            {canonicalFoodSearch.isLoadingMore ? (
              <View style={styles.loadingFooter}>
                <LoadingLogo
                  container={false}
                  width={50}
                  height={50}
                  dark={true}
                />
              </View>
            ) : (
              <View style={styles.loadingSpacer} />
            )}
          </View>
        ) : null}
      </View>

      {isFoodModalVisible ? (
        <AddFoodModal
          isVisible={isFoodModalVisible}
          onClose={closeFoodModal}
          coach={coach}
          food={modalFood}
        />
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  headerCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    minHeight: 50,
    paddingVertical: 10,
    gap: 10,
  },
  searchField: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    cursor: "text",
  },
  searchIcon: {
    justifyContent: "center",
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    marginLeft: 10,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  searchInputIOS: {
    fontSize: 16,
  },
  divider: {
    height: 0.5,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 14,
  },
  bottomRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 12,
  },
  filters: {
    flexDirection: "row",
    flexWrap: "wrap",
    flex: 1,
    minWidth: 0,
  },
  filterItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    marginRight: 4,
  },
  filterItemActive: {
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  filterText: {
    fontSize: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginLeft: 6,
  },
  filterTextActive: {
    color: Colors.bubbleColor,
  },
  createButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.bubbleColor,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    flexShrink: 0,
    transition: "opacity 180ms ease-in-out",
  },
  createButtonHovered: {
    opacity: 0.6,
  },
  createButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
    marginLeft: 6,
  },
  sortTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    flexShrink: 0,
  },
  sortTriggerLabel: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  sortTriggerLabelHovered: {
    color: Colors.darkgrayColor,
  },
  sortTriggerValue: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  sortMenuItem: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  sortMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  sortMenuItemText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  sortMenuItemTextActive: {
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  results: {
    marginTop: 24,
    marginBottom: 36,
  },
  searchPendingState: {
    minHeight: 280,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
  },
  loadMoreSentinel: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingFooter: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 10,
  },
  loadingSpacer: {
    width: "100%",
    height: 32,
  },
  foodCardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  cardTouchable: {
    borderRadius: 12,
    overflow: "hidden",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
  },
  card: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    paddingVertical: 16,
    paddingHorizontal: 18,
    overflow: "hidden",
    transitionProperty: "border-color, background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  cardHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: "#FDFDFD",
  },
  cardEnterTarget: {
    borderColor: Colors.darkgrayColor,
    backgroundColor: "#FDFDFD",
  },
  cardLeft: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  cardName: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
    color: Colors.blackColor,
  },
  cardDatabase: {
    fontSize: 10,
    lineHeight: 14,
    color: Colors.grayColor,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    marginTop: 3,
  },
  cardDatabaseCreatedByMe: {
    color: Colors.bubbleColor,
  },
  cardRight: {
    alignItems: "flex-end",
    gap: 4,
  },
  cardKcal: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  cardKcalUnit: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  cardMacroRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  cardMacroValue: {
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    color: Colors.grayColor,
  },
  cardMacroNum: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
});
